import { useQueries, useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next"
import { toast } from "react-toastify";
import { Col, Label, Row } from "reactstrap";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";
import TaskControlsService from "src/modules/data-protection/apis/TaskControlsService";
import Select from "react-select";
import { ControlsFrameworks } from "src/modules/data-protection/constants";
import { formatControlNumber } from "src/modules/data-protection/helpers/Controls";

const ISMSControls = ({
    t,
    lng,
    controlsOptions,
    onControlsChange,
    defaultValues
}) => {
    const ismsAccessibleControlsQuery = useQuery({
        queryKey: [
            'gdpr-fetch-isms-accessible-controls',
            controlsOptions
        ],
        queryFn: async () => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.fetchAccessibleControls({
                ...controlsOptions
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while retrieving the controls list.'), {
                type: 'error'
            });
        }
    });

    const {
        accessible_frameworks = []
    } = ismsAccessibleControlsQuery.data || {};

    const controlsQueries = useQueries({
        queries: accessible_frameworks.map(framework => ({
            queryKey: [`gdpr-controls`, lng, framework],
            queryFn: async () => {
                const service = TaskControlsService.getInstance();

                const response = await service.fetchControls({
                    type: framework
                });

                return (response || []).map((control) => {
                    return {
                        value: control.id,
                        label: `${formatControlNumber(control.control_number, framework)}. ${t(control.title)}`,
                        baseLabel: control.title
                    }
                })
            },
            enabled: ismsAccessibleControlsQuery.isFeched && ismsAccessibleControlsQuery.isSuccess,
            refetchOnWindowFocus: false
        }))
    });

    const controlsQueriesResults = accessible_frameworks.map((frameworkKey, index) => ({
        ...controlsQueries[index],
        frameworkName: frameworkKey
    }))

    if (ismsAccessibleControlsQuery.isFetching) {
        return (
            <Row>
                <Col sm="6">
                    <div
                        className="dt-field dt-skeleton dt-select-list"
                        style={{ marginBottom: 16 }}
                    ></div>
                </Col>

                <Col sm="6">
                    <div
                        className="dt-field dt-skeleton dt-select-list"
                        style={{ marginBottom: 16 }}
                    ></div>
                </Col>
            </Row>
        )
    }

    return (
        <Row>
            {controlsQueriesResults.map((controlQuery) => {
                const controleFrameworkInfo = ControlsFrameworks.find((framework) => framework.key === controlQuery.frameworkName);

                return (
                    <Col sm="12" md="6" className="mb-3">
                        <Label className="form-label">
                            {t(controleFrameworkInfo?.title)}
                        </Label>
                        {controlQuery.isFetching || controlQuery.isLoading ? (
                            <div
                                className="dt-field dt-skeleton dt-select-list"
                                style={{ marginBottom: 16 }}
                            ></div>
                        ) : (
                            <Select
                                isMulti
                                name="control"
                                classNamePrefix="select2-selection"
                                options={controlQuery.data || []}
                                placeholder={t("Select")}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                                onChange={(e) => {
                                    onControlsChange((prev) => {
                                        const oldValues = prev || {};

                                        return {
                                            ...oldValues,
                                            [controlQuery.frameworkName]: e.map((itemOption) => itemOption.value)
                                        }
                                    })
                                }}
                                menuPortalTarget={document.body}
                                defaultValue={(defaultValues?.[controlQuery.frameworkName] ?? []).map((cItemId) => {
                                    return (controlQuery.data || []).find((option) => option.value === cItemId)
                                })}
                            />
                        )}
                    </Col>
                )
            })}
        </Row>
    );
}

export default withNamespaces()(
    ISMSControls
)